import { Color } from '@persona-id/design-tokens';
import { keyBy, startCase } from 'lodash/fp';
import { ComponentProps, FC, memo, useCallback, useContext, useMemo, useState } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { Payload } from 'recharts/types/component/DefaultLegendContent';
import { ThemeContext } from 'styled-components';

import ChartLayout, {
  ChartLegendContent,
  ChartTooltipContainer,
  ChartTooltipHeader,
  ChartTooltipLabel,
  ChartTooltipLabelContainer,
} from 'dashboard/layouts/ChartLayout';
import { TableNullState } from 'dashboard/layouts/TableLayoutV2';
import { VERIFICATION_TYPE_NAMES_SHORT } from 'dashboard/lib/constants/verification';
import { formatNumber, formatNumberLarge } from 'dashboard/lib/utils/formatters';
import {
  FunnelStepStatus,
  FunnelStepType,
  MetricsTemplateFunnel,
  TemplateFunnelDatum,
} from 'dashboard/models/metrics';
import { Verification } from 'dashboard/models/verification';
import { Heading } from 'design-system/components/Text';
import { TransitionDuration } from 'design-system/variables/transitions';
import Dot from 'images/dashboard/dot.svg';
import useToggle from 'lib/hooks/useToggle';
import { ApiModel } from 'lib/interfaces/api';
import { Step, StepVerification, UiStepType } from 'lib/interfaces/StepsConfig';

interface TemplateFunnelChartProps {
  data: MetricsTemplateFunnel['data'];
  onDeepLink: (datum: TemplateFunnelDatum, templateId: string) => void;
  steps: Step[];
  templateId?: string;
}

type BarType = 'count' | 'dropoff';

const DEFAULT_DIMENSION_MARGIN = 16;

const DIMENSIONS = {
  height: 320,
  margin: {
    top: DEFAULT_DIMENSION_MARGIN * 2,
    bottom: DEFAULT_DIMENSION_MARGIN / 2,
    left: -DEFAULT_DIMENSION_MARGIN,
  },
};

const TemplateFunnelChart = ({ data, onDeepLink, steps, templateId }: TemplateFunnelChartProps) => {
  const theme = useContext(ThemeContext);

  /**
   * We can only accurately compute and display funnel metrics for templates
   * with linear step configs. This is because we assume steps are serial.
   *
   * Linear step configs always go to a failed step on failed. The alternatives
   * are configs that succeed on pass and go to another verification on failed
   * (fallback), or configs that go to verifications on either pass or fail
   * (forking logic).
   */
  const stepsMap = keyBy((s) => s.name, steps);
  const isLinear = useMemo(
    () =>
      steps
        .filter((s): s is StepVerification => (s as StepVerification).on?.failed != null)
        .every((s) => {
          const failedStepType = stepsMap[s.on.failed].type;
          return failedStepType === UiStepType.Failed || failedStepType === s.type;
        }),
    [steps],
  );

  const [filteredBarTypes, setFilteredBarTypes] = useState<BarType[]>([]);

  const [legendHovered, { enable: setLegendHovered, disable: clearLegendHovered }] = useToggle();
  const [hoveredKey, setHoveredKey] = useState<BarType | null>(null);

  const handleLegendClick = useCallback(
    ({ dataKey }: Payload) =>
      typeof dataKey === 'string' &&
      setFilteredBarTypes(
        filteredBarTypes.includes(dataKey as BarType)
          ? filteredBarTypes.filter((s) => s !== dataKey)
          : filteredBarTypes.concat(dataKey as BarType),
      ),
    [filteredBarTypes],
  );

  const handleLegendMouseEnter = useCallback(({ dataKey }: Payload) => {
    setLegendHovered();
    typeof dataKey === 'string' && setHoveredKey(dataKey as BarType);
  }, []);
  const handleLegendMouseLeave = useCallback(() => {
    clearLegendHovered();
    setHoveredKey(null);
  }, []);

  const handleDeepLink = useCallback(
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    (_: unknown, index: number) => onDeepLink(data[index], templateId),
    [onDeepLink, data, templateId],
  );

  if (!isLinear) {
    return (
      <TableNullState dataTestBlock='template-funnel-chart'>
        <Heading size='sm' data-test='template-funnel-chart__nonlinear'>
          Metrics cannot be displayed for this template because its flow is not linear.
        </Heading>
      </TableNullState>
    );
  }

  return (
    <ChartLayout data-test='template-funnel-chart__chart'>
      <ResponsiveContainer height={DIMENSIONS.height}>
        <BarChart data={data} margin={DIMENSIONS.margin}>
          <CartesianGrid vertical={false} stroke={theme.color.border.secondary} />
          <XAxis
            dataKey='type'
            axisLine={{ stroke: 'none' }}
            tickLine={{ stroke: 'none' }}
            tickFormatter={(_: unknown, i: number) =>
              data[i] == null ? '' : formatLabel(data[i].type, data[i].status)
            }
            tick={{
              fontSize: theme.text.paragraph.mdBold.fontSize,
              fill: theme.color.text.tertiary,
              width: 75,
            }}
            interval={0}
          />
          <YAxis
            axisLine={{ stroke: 'none' }}
            tickLine={{ stroke: 'none' }}
            tickFormatter={formatNumberLarge}
            tick={{
              fontSize: theme.text.paragraph.mdBold.fontSize,
              fill: theme.color.text.tertiary,
            }}
            allowDecimals={false}
          />
          <Tooltip
            isAnimationActive={false}
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            content={<TooltipContent hoveredKey={hoveredKey} />}
            allowEscapeViewBox={{
              x: true,
            }}
          />
          <Legend
            layout='vertical'
            verticalAlign='top'
            align='right'
            formatter={startCase}
            iconType='circle'
            inactiveColor={Color.CoreSlate50}
            // eslint-disable-next-line rulesdir/jsx-click-tracking-ids -- this element doesn't take ID
            onClick={handleLegendClick}
            onMouseEnter={handleLegendMouseEnter}
            onMouseLeave={handleLegendMouseLeave}
            content={<ChartLegendContent />}
          />
          <Bar
            dataKey='count'
            stackId='templateFunnel'
            // This element doesn't take ID and the library types are wrong
            // eslint-disable-next-line rulesdir/jsx-click-tracking-ids, @typescript-eslint/no-explicit-any
            onClick={handleDeepLink as any}
            onMouseEnter={() => setHoveredKey('count')}
            onMouseLeave={() => setHoveredKey(null)}
            fill={Color.CorePurple600}
            fillOpacity={!legendHovered || hoveredKey == null || hoveredKey === 'count' ? 1 : 0.5}
            stroke={legendHovered && hoveredKey === 'count' ? theme.color.border.active : undefined}
            hide={filteredBarTypes.length > 0 && !filteredBarTypes.includes('count')}
            style={{
              transition: `fill-opacity ${TransitionDuration.Fast}, stroke ${TransitionDuration.Fast}`,
            }}
          />
          <Bar
            dataKey='dropoff'
            stackId='templateFunnel'
            // This element doesn't take ID and the library types are wrong
            // eslint-disable-next-line rulesdir/jsx-click-tracking-ids, @typescript-eslint/no-explicit-any
            onClick={handleDeepLink as any}
            onMouseEnter={() => setHoveredKey('dropoff')}
            onMouseLeave={() => setHoveredKey(null)}
            fill={Color.CorePurple100}
            fillOpacity={0.5}
            stroke={
              legendHovered && hoveredKey === 'dropoff' ? theme.color.border.active : undefined
            }
            hide={filteredBarTypes.length > 0 && !filteredBarTypes.includes('dropoff')}
            style={{
              transition: `fill-opacity ${TransitionDuration.Fast}, stroke ${TransitionDuration.Fast}`,
            }}
          />
        </BarChart>
      </ResponsiveContainer>
    </ChartLayout>
  );
};

export default memo(TemplateFunnelChart);

const TooltipContent: FC<ComponentProps<typeof Tooltip> & { hoveredKey: BarType }> = ({
  active,
  payload,
  label,
  hoveredKey,
}) => {
  if (!active || payload == null) {
    return null;
  }

  const {
    payload: { count, dropoff, fraction_completed: fractionCompleted, status },
  } = payload[0] as any;

  return (
    <ChartTooltipContainer>
      <ChartTooltipHeader>{formatLabel(label, status)}</ChartTooltipHeader>

      {dropoff != null && (
        <ChartTooltipLabelContainer
          active={hoveredKey === 'dropoff'}
          hasActive={hoveredKey != null}
        >
          <ChartTooltipLabel>
            <Dot style={{ color: Color.CorePurple100 }} /> Dropoff
          </ChartTooltipLabel>
          <ChartTooltipLabel>{formatNumber(dropoff)}</ChartTooltipLabel>
          <ChartTooltipLabel>
            {formatNumber(1 - fractionCompleted, {
              style: 'percent',
            })}
          </ChartTooltipLabel>
        </ChartTooltipLabelContainer>
      )}

      <ChartTooltipLabelContainer active={hoveredKey === 'count'} hasActive={hoveredKey != null}>
        <ChartTooltipLabel>
          <Dot style={{ color: Color.CorePurple600 }} /> Count
        </ChartTooltipLabel>
        <ChartTooltipLabel>{formatNumber(count)}</ChartTooltipLabel>
        <ChartTooltipLabel>
          {formatNumber(fractionCompleted, { style: 'percent' })}
        </ChartTooltipLabel>
      </ChartTooltipLabelContainer>
    </ChartTooltipContainer>
  );
};

function formatLabel(stepType: FunnelStepType, status: FunnelStepStatus): string {
  let formattedType: string;
  switch (stepType) {
    case 'created':
    case 'completed':
      formattedType = startCase(stepType);
      break;
    default:
      formattedType = VERIFICATION_TYPE_NAMES_SHORTEST[stepType];
  }
  return status != null ? `${formattedType} (${startCase(status)})` : formattedType;
}

export const VERIFICATION_TYPE_NAMES_SHORTEST: Record<Verification['type'], string> = {
  ...VERIFICATION_TYPE_NAMES_SHORT,
  [ApiModel.VerificationGovernmentId]: 'Gov. ID',
};
